import readEventStream from '@Infra/EventStream/readEventStream';
import type { DefaultPayload } from '@verclam/synapse';

export type EventStreamOpenedAction = {
	type: 'EVENT_STREAM_OPENED';
	payload: { url: string };
};

type Payload = NonNullable<DefaultPayload['payload']>;

const isPayload = (value: unknown): value is Payload => typeof value === 'object' && value !== null;

const parsePayload = (data: string): Payload | null => {
	try {
		const parsed: unknown = JSON.parse(data);

		return isPayload(parsed) ? parsed : null;
	} catch {
		return null;
	}
};

export type EventStreamSubscription<Event extends DefaultPayload> = {
	/** Action to dispatch to open the stream. */
	start: (dispatch: (action: Event | EventStreamOpenedAction) => void) => Promise<EventStreamOpenedAction>;
	/** Ends the read; nothing is dispatched once it returns. Safe to call more than once. */
	close: () => void;
};

/**
 * `start` opens the stream and returns at once: the read lasts until `close` is called. Each frame
 * whose `event` is in `eventTypes` is dispatched as `{ type: event, payload: data }`, the parsed JSON
 * being the action payload as received; any other event (the heartbeat included) is ignored.
 * `reconnectionUrl`, when given, is the target of the attempts that follow a lost connection.
 */
export default <Event extends DefaultPayload>(
	url: string,
	eventTypes: readonly Event['type'][],
	reconnectionUrl?: string,
): EventStreamSubscription<Event> => {
	const controller = new AbortController();

	const start = async (
		dispatch: (action: Event | EventStreamOpenedAction) => void,
	): Promise<EventStreamOpenedAction> => {
		void readEventStream(url, {
			signal: controller.signal,
			...(reconnectionUrl === undefined ? {} : { reconnectionUrl }),
			onEvent: ({ event, data }) => {
				if (controller.signal.aborted) {
					return;
				}

				if (!(eventTypes as readonly string[]).includes(event)) {
					return;
				}

				const payload = parsePayload(data);

				if (payload !== null) {
					dispatch({ type: event, payload } as Event);
				}
			},
		});

		const action: EventStreamOpenedAction = { type: 'EVENT_STREAM_OPENED', payload: { url } };
		dispatch(action);
		return action;
	};

	return { start, close: () => controller.abort() };
};
